import React, { useMemo } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Badge } from '../components/ui/badge';
import { Card } from '../components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { IndianRupee, Building2, User, Clock } from 'lucide-react';

const STATUS_CONFIG = {
  new: { label: 'New', color: 'bg-slate-100 border-slate-300', header: 'bg-slate-600', probability: 10 },
  contacted: { label: 'Contacted', color: 'bg-blue-50 border-blue-200', header: 'bg-blue-600', probability: 20 },
  qualified: { label: 'Qualified', color: 'bg-yellow-50 border-yellow-200', header: 'bg-yellow-600', probability: 40 },
  follow_up_1: { label: 'Follow Up 1', color: 'bg-indigo-50 border-indigo-200', header: 'bg-indigo-500', probability: 50 },
  follow_up_2: { label: 'Follow Up 2', color: 'bg-indigo-50 border-indigo-200', header: 'bg-indigo-600', probability: 60 },
  follow_up_3: { label: 'Follow Up 3', color: 'bg-indigo-50 border-indigo-200', header: 'bg-indigo-700', probability: 70 },
  proposal: { label: 'Proposal', color: 'bg-purple-50 border-purple-200', header: 'bg-purple-600', probability: 80 },
  won: { label: 'Won', color: 'bg-green-50 border-green-200', header: 'bg-green-600', probability: 100 },
  lost: { label: 'Lost', color: 'bg-red-50 border-red-200', header: 'bg-red-500', probability: 0 },
};

const PIPELINE_STATUSES = ['new', 'contacted', 'qualified', 'follow_up_1', 'follow_up_2', 'follow_up_3', 'proposal', 'won', 'lost'];

const formatCurrency = (val) => {
  if (!val && val !== 0) return '';
  const num = Number(val);
  if (isNaN(num)) return '';
  if (num >= 10000000) return `${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString('en-IN');
};

const LeadCard = React.memo(({ lead, index, onSelect }) => {
  const days = lead.days_since_status_change || 0;
  const dealSize = lead.estimated_deal_size;

  return (
    <Draggable draggableId={lead.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={`mb-2 transition-shadow ${snapshot.isDragging ? 'shadow-lg ring-2 ring-primary/30' : ''}`}
          onClick={() => onSelect?.(lead)}
        >
          <Card className="p-3 cursor-pointer hover:shadow-md transition-all border-l-4" style={{ borderLeftColor: days >= 7 ? '#ef4444' : days >= 3 ? '#f59e0b' : '#e2e8f0' }}>
            <div className="flex items-center justify-between mb-0.5">
              <p className="font-medium text-sm truncate flex-1 mr-1" data-testid={`kanban-lead-${lead.id}`}>{lead.name}</p>
              {lead.lead_score > 0 && (
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded text-white cursor-help ${lead.lead_grade === 'A' ? 'bg-green-600' : lead.lead_grade === 'B' ? 'bg-blue-600' : lead.lead_grade === 'C' ? 'bg-yellow-600' : 'bg-red-600'}`}>{lead.lead_grade}</span>
                    </TooltipTrigger>
                    <TooltipContent side="left" className="max-w-[240px] p-2.5 bg-slate-900 text-white rounded-lg">
                      <p className="font-bold text-xs mb-1">{lead.lead_grade === 'A' ? 'Hot Lead' : lead.lead_grade === 'B' ? 'Warm Lead' : lead.lead_grade === 'C' ? 'Moderate' : 'Cool Lead'} — {lead.lead_score}/100</p>
                      {lead.score_factors?.length > 0 && (
                        <ul className="space-y-0.5">{lead.score_factors.map((f, i) => <li key={i} className="text-[10px] opacity-85">• {f}</li>)}</ul>
                      )}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
            </div>
            {lead.company && (
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                <Building2 className="w-3 h-3" /><span className="truncate">{lead.company}</span>
              </div>
            )}
            <div className="flex items-center justify-between mt-2 gap-1">
              {dealSize ? (
                <span className="flex items-center gap-0.5 text-xs font-semibold text-emerald-700">
                  <IndianRupee className="w-3 h-3" />{formatCurrency(dealSize)}
                </span>
              ) : <span />}
              <div className="flex items-center gap-1">
                {lead.assigned_to_name && (
                  <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground"><User className="w-2.5 h-2.5" />{lead.assigned_to_name.split(' ')[0]}</span>
                )}
                {days > 0 && (
                  <span className={`flex items-center gap-0.5 text-[10px] ${days >= 7 ? 'text-red-600' : days >= 3 ? 'text-orange-600' : 'text-muted-foreground'}`}>
                    <Clock className="w-2.5 h-2.5" />{days}d
                  </span>
                )}
              </div>
            </div>
          </Card>
        </div>
      )}
    </Draggable>
  );
});

export default function CRMKanbanBoard({ leads, onStatusChange, onSelectLead }) {
  const columns = useMemo(() => {
    const cols = {};
    PIPELINE_STATUSES.forEach(status => {
      cols[status] = { ...STATUS_CONFIG[status], leads: [] };
    });
    (leads || []).forEach(lead => {
      const status = lead.status || 'new';
      if (cols[status]) cols[status].leads.push(lead);
    });
    // Sort by deal size desc within each column
    Object.values(cols).forEach(col => {
      col.leads.sort((a, b) => (b.estimated_deal_size || 0) - (a.estimated_deal_size || 0));
    });
    return cols;
  }, [leads]);

  const columnTotals = useMemo(() => {
    const totals = {};
    PIPELINE_STATUSES.forEach(status => {
      totals[status] = columns[status].leads.reduce((sum, l) => sum + (l.estimated_deal_size || 0), 0);
    });
    return totals;
  }, [columns]);

  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { draggableId, destination } = result;
    const newStatus = destination.droppableId;
    onStatusChange?.(draggableId, newStatus);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-3 overflow-x-auto pb-4 scrollbar-thin" style={{ minHeight: '500px', WebkitOverflowScrolling: 'touch' }} data-testid="kanban-board">
        {PIPELINE_STATUSES.map(status => {
          const col = columns[status];
          const total = columnTotals[status];
          return (
            <div key={status} className={`flex-shrink-0 w-52 rounded-lg border ${col.color}`}>
              <div className={`${col.header} text-white px-3 py-2 rounded-t-lg`}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold">{col.label}</span>
                  <Badge variant="secondary" className="text-[10px] h-5 bg-white/20 text-white">{col.leads.length}</Badge>
                </div>
                {total > 0 && (
                  <div className="text-[10px] opacity-80 mt-0.5 flex items-center gap-0.5">
                    <IndianRupee className="w-2.5 h-2.5" />{formatCurrency(total)}
                  </div>
                )}
              </div>
              <Droppable droppableId={status}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={`p-2 min-h-[400px] transition-colors ${snapshot.isDraggingOver ? 'bg-primary/5' : ''}`}
                  >
                    {col.leads.map((lead, index) => (
                      <LeadCard key={lead.id} lead={lead} index={index} onSelect={onSelectLead} />
                    ))}
                    {provided.placeholder}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
